export type CompositionLine = { name: string; quantity: number };

function rawToJsonString(raw: string | null | undefined | unknown): string | null {
  if (raw == null) return null;
  if (typeof raw === 'string') return raw.trim() || null;
  if (typeof raw === 'object') return JSON.stringify(raw);
  return String(raw).trim() || null;
}

/**
 * Парсит состав (JSON или текст) в массив строк для отображения как на сайте:
 * название + пунктир + "N шт."
 * Принимает строку, массив или объект (при объекте/массиве сериализует в JSON).
 */
export function parseCompositionLines(raw: string | null | undefined | unknown): CompositionLine[] {
  const str = rawToJsonString(raw);
  if (!str) return [];
  const trimmed = str.trim();
  if (!trimmed) return [];

  try {
    const first = trimmed.charAt(0);
    if (first === '[' || first === '{') {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed) && parsed.length > 0) {
        return parsed
          .map((item: unknown): CompositionLine | null => {
            if (item && typeof item === 'object') {
              const obj = item as Record<string, unknown>;
              const name = typeof obj.sostav === 'string' ? obj.sostav : typeof obj.name === 'string' ? obj.name : null;
              const qty = obj.koliches ?? obj.quantity ?? obj.qty;
              const num = typeof qty === 'number' ? qty : typeof qty === 'string' ? parseInt(qty, 10) : null;
              if (name) {
                const quantity = Number.isFinite(num) && num != null ? num : 1;
                return { name, quantity };
              }
            }
            return null;
          })
          .filter((x): x is CompositionLine => x != null);
      }
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        const name = (parsed as Record<string, unknown>).sostav ?? (parsed as Record<string, unknown>).name;
        if (typeof name === 'string') return [{ name, quantity: 1 }];
      }
    }
  } catch {
    // не JSON
  }

  // Текстовый формат: "Розы 51 шт." или "51 роза микс" — извлекаем число, иначе будет 1 шт.
  const matchEndSh = trimmed.match(/^(.+?)\s+(\d+)\s*шт\.?\s*$/i);
  if (matchEndSh) {
    const name = matchEndSh[1].trim();
    const qty = parseInt(matchEndSh[2], 10);
    if (name && Number.isFinite(qty) && qty > 0) return [{ name, quantity: qty }];
  }
  const matchStartNum = trimmed.match(/^(\d+)\s+(.+)$/);
  if (matchStartNum) {
    const qty = parseInt(matchStartNum[1], 10);
    const name = matchStartNum[2].trim();
    if (name && Number.isFinite(qty) && qty > 0) return [{ name, quantity: qty }];
  }

  return [{ name: trimmed, quantity: 1 }];
}

/**
 * Преобразует состав в одну строку (для списков, профиля).
 * Принимает строку, массив или объект.
 */
/**
 * Если в составе одна позиция с quantity 1, пробуем взять число из названия товара (напр. "51 роза микс" → 51).
 */
export function tryInferQuantityFromTitle(
  lines: CompositionLine[],
  productName: string | null | undefined
): CompositionLine[] {
  if (lines.length !== 1 || lines[0].quantity !== 1 || !productName?.trim()) return lines;
  const numMatch = productName.match(/\b(\d+)\s*(?:роз[аыей]|шт\.?|штук)/i) || productName.match(/^(\d+)\s+/);
  const qty = numMatch ? parseInt(numMatch[1], 10) : 0;
  if (qty > 1) return [{ name: lines[0].name, quantity: qty }];
  return lines;
}

export function formatComposition(raw: string | null | undefined | unknown): string | null {
  const lines = parseCompositionLines(raw);
  if (lines.length === 0) return null;
  return lines.map((l) => (l.quantity > 1 ? `${l.name} ${l.quantity} шт.` : l.name)).join(', ');
}
